import { DirectoryIntro } from '~/ui-rocco/pages/directory-browse/DirectoryIntro';
import { GetMatchedHost } from '~/modules-rocco/get-matched/GetMatchedHost';
import type { GetMatchedEnquiryType } from '~/modules-rocco/get-matched/GetMatchedHost';
import type { GetMatchedStyleImage } from '~/modules/get-matched/api/get-matched-style-images.server';
import {
    CONTENT_CAP_PADDED,
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';

// The module's premise is "you probably won't enquire with this supplier" —
// blank the island while it crosses the reading line rather than floating the
// enquiry pill over the very moment that offers the alternative.
const SUPPRESS_ISLAND: IslandMode = { kind: 'hidden' };

// The brief matches product readers with suppliers and everyone else with
// professionals — the copy names whichever the flow actually delivers.
const COPY: Record<
    GetMatchedEnquiryType,
    { heading: string; caption: string }
> = {
    product: {
        heading:
            "Can't find what you're after? Get Matched lets you describe what you need and have matching suppliers come to you.",
        caption:
            "Can't find it? Describe what you need and matching suppliers will come to you.",
    },
    professional: {
        heading:
            "Can't find what you're after? Get Matched lets you describe what you need and have matching professionals come to you.",
        caption:
            "Can't find it? Describe what you need and matching professionals will come to you.",
    },
};

interface GetMatchedBandProps {
    /**
     * Which brief this module starts. A project detail passes `professional`:
     * the match is with a practice, and matching a reader to another project
     * serves nobody.
     */
    enquiryType: GetMatchedEnquiryType;
    /** Style imagery for the brief's style step (loader-fetched, like the
     *  browse routes) — without it the tiles fall back to their ground. */
    styleImages?: GetMatchedStyleImage[];
}

/**
 * The detail pages' closing module — the category pages' Get Matched grid
 * injection rendered as the page's last call, on both devices. A reader who
 * has passed every enquiry prompt without acting probably won't enquire with
 * this supplier or professional, so the exit offers describing the project
 * once and letting matching suppliers or professionals come to them — the
 * same module and layout the directory grids run.
 *
 * Mounts its own {@link GetMatchedHost} beside the trigger, because the brief
 * is mounted per-surface rather than globally. Ships with the revamp: both
 * pages render behind details_revamp_2026.
 */
export const GetMatchedBand = ({
    enquiryType,
    styleImages,
}: GetMatchedBandProps) => {
    const suppressRef = useIslandSection<HTMLElement>(SUPPRESS_ISLAND);
    const copy = COPY[enquiryType];

    return (
        <section
            ref={suppressRef}
            className="w-full bg-white px-4 md:px-[3rem]"
        >
            <div className={CONTENT_CAP_PADDED}>
                <DirectoryIntro
                    variant="grid-injection"
                    eyebrow="Find the perfect solution"
                    heading={copy.heading}
                    caption={copy.caption}
                    // The statement right above already carries this message —
                    // stacked on mobile the pair reads as repetition.
                    hideMobileCaption
                />
                {/* Mobile has no closing banner below to hold this module apart
                    from the editorial closing's text, so it draws its own
                    hairline after a healthy pause. Desktop's closing opens with
                    a full-bleed image, which separates on its own. */}
                <div
                    aria-hidden
                    className="mt-12 h-px w-full bg-black/10 md:hidden"
                />
            </div>
            <GetMatchedHost
                enquiryType={enquiryType}
                styleImages={styleImages}
            />
        </section>
    );
};
